import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { Editor } from '@tiptap/react';

import type { AppSettingsDoc, UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';

import type { DocumentWithType, SidebarOverlayData } from '@/constants/types';

export const multiSelectionsAtom = atom<DocumentWithType[] | null>(null);

export const editorAtom = atom<Editor | null>(null);

export const appReadyAtom = atom(false);

export const initialSyncCompleteAtom = atom(false);

export const initialSyncResolvedAtom = atom(false);
export const initialSyncBannerVisibleAtom = atom(false);
export const initialSyncBannerProgressAtom = atom(0);

export const emptyLayoutAtom = atom(false);

export const textGenerationMapAtom = atom<Record<string, { focusPos: number; prompt: string }>>({});

export const getTextGenerationKey = ({ documentId, editorType }: { documentId?: string; editorType: string }): string =>
  documentId ?? editorType;

export const sidebarOverlayAtom = atom<SidebarOverlayData | null>(null);

export const syncProgressAtom = atom<number>(0);
export const showSyncModalAtom = atom<boolean>(false);

export const externalWorkspaceTabStateAtom = atom<{ tab: string; focusedInstructionGroupId?: string } | null>(null);

export const taggedDocsMultiSelectionsAtom = atom<DocumentWithType[] | null>(null);

export interface Suggestion {
  id: string;
  range: { from: number; to: number };
  metadata?: any;
}

// Data-derived "no documents loaded" (written by DocumentWorkspace from query results,
// drives StartingPointSelector/empty-state UI) — distinct from knownEmptyAccountAtom below.
export const emptyAccountAtom = atom(true);

// "This account is known/assumed to have no documents" — written only by the local-data
// layer (empty-account probe, see local-data-service.ts). Consumers may skip
// loading/skeleton/banner UI when true, but must never hide real data behind it.
export const knownEmptyAccountAtom = atom(false);

export const chatbotOpenAtom = atom(false);

// Persisted, but not live-synced: without `subscribe` the atom ignores other tabs' storage events, so
// collapsing the sidebar for room in one tab does not slam it shut in every other open tab.
const leftSidebarCollapsedStorage = createJSONStorage<boolean>(() => localStorage);
delete leftSidebarCollapsedStorage.subscribe;

/**
 * The user's choice to collapse the left sidebar, shared by every route whose left sidebar is collapsible.
 * `getOnInit` reads storage before the first paint, so a collapsed sidebar never flashes open on load.
 */
export const leftSidebarCollapsedAtom = atomWithStorage('left-sidebar-collapsed', false, leftSidebarCollapsedStorage, {
  getOnInit: true,
});

/** Which right sidebar the DocuAI launcher docks into: the workspace layout's, or the block editor overlay's. */
export type DocuAiDockColumn = 'layout' | 'block-editor';
/** Space a sidebar reserves for the DocuAI launcher: hidden, a drop placeholder while dragging, or the docked launcher. */
export type DocuAiDockSlotState = 'hidden' | 'placeholder' | 'docked';
export const docuAiDockAtom = atom<{ column: DocuAiDockColumn | null; state: DocuAiDockSlotState }>({
  column: null,
  state: 'hidden',
});
/** The mounted dock column elements, registered by each DocuAiDockSlot; null while a column is absent. */
export const docuAiDockColumnsAtom = atom<Record<DocuAiDockColumn, HTMLElement | null>>({ layout: null, 'block-editor': null });

/**
 * Per LLM request id: true once the user has opened the chat since that request's last run finished.
 * Lives outside the chat surface so a run that ends while the surface is unmounted (the block editor
 * overlay swaps the note chat for its own) still reports its outcome on the launcher when it returns.
 */
export const chatLauncherOutcomeSeenAtom = atom<Record<string, boolean>>({});

export const pendingUserSourceResyncsAtom = atom<Record<string, boolean>>({});

/** The `settings/app` values, defaulted when missing; `undefined` until loaded. */
export const appSettingsAtom = atom<Omit<AppSettingsDoc, 'updatedAt'> | undefined>(undefined);

/** The signed-in user's live `userAccounts` doc; `loading` until the server answers. */
export const userAccountAtom = atom<{ account: UserAccountDoc | undefined; loading: boolean }>({
  account: undefined,
  loading: true,
});

/** Whether the user closed the "You're using Free" editor banner; survives moving between documents, resets on reload. */
export const freeAccessBannerDismissedAtom = atom(false);

/** Whether the user closed the "Your Trial is still active" editor banner; survives moving between documents, resets on reload. */
export const trialEnergyBannerDismissedAtom = atom(false);

/** Whether the user closed the cancelled Trial's "Your Trial ends on" editor banner; survives moving between documents, resets on reload. */
export const cancelledTrialBannerDismissedAtom = atom(false);

/**
 * Whether the AI refusal invitation is open; opened through `useOpenAiRefusalInvitation`, which admits only Free
 * accounts, Trials with their Energy used up and renewals in their grace period.
 */
export const aiRefusalInvitationOpenAtom = atom(false);

/** The `currentPeriodStart` of the renewal grace period in which AI was last refused this session: its grace allowance is used up. */
export const graceAllowanceUsedUpPeriodAtom = atom<string | undefined>(undefined);

/** Ids of user sources this client created for processing this session, until their processing settles. */
export const processingUploadIdsAtom = atom<string[]>([]);
